import React, { useState, useEffect, useRef } from 'react';
import { 
  Building, Calendar as CalendarIcon, Clock, Globe, User, Phone, Mail, MapPin,
  GraduationCap, Flag, BookOpen, Award, CheckCircle, Video, Mic, MicOff,
  Volume2, VolumeX, MessageSquare, Bot, Sparkles, X, Play, RefreshCw,
  Send, ExternalLink, CalendarPlus, ChevronRight, ShieldCheck, Headphones, Menu,
  GraduationCap as LogoIcon
} from 'lucide-react';
import emailjs from '@emailjs/browser';

const AVAILABLE_SLOTS = [
  "09:00 AM", "10:00 AM", "11:00 AM", "11:30 AM",
  "12:00 PM", "01:00 PM", "02:00 PM", "02:30 PM",
  "03:00 PM", "04:00 PM", "05:00 PM", "05:30 PM",
  "06:00 PM", "07:00 PM", "08:00 PM", "09:00 PM"
];

const inputStyle = {
  width: '100%',
  marginTop: '0.35rem',
  padding: '0.75rem 0.85rem',
  borderRadius: '8px',
  border: '1px solid #2D3748',
  backgroundColor: '#0F172A',
  color: '#F8FAFC',
  fontSize: '0.9rem',
  boxSizing: 'border-box',
  outline: 'none'
};

export default function App() {
  // --- Screen Size Detector for Responsive Styles ---
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // --- Date Setup ---
  const getTodayLocalString = () => {
    const today = new Date();
    const offset = today.getTimezoneOffset();
    const localToday = new Date(today.getTime() - (offset * 60 * 1000));
    return localToday.toISOString().split('T')[0];
  };

  const [selectedDate, setSelectedDate] = useState(getTodayLocalString);
  const [selectedSlot, setSelectedSlot] = useState("10:00 AM");
  const [bookingResult, setBookingResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // --- Booking Form State ---
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    state: '',
    highestQualification: "Bachelor's Degree",
    countryOfChoice: 'Canada',
    academicDetails: '',
    hasLanguageProficiency: 'Yes',
    languageCertificateType: 'IELTS',
    languageScore: ''
  });

  // --- AI Voice & Chat Agent State ---
  const [isVoiceChatOpen, setIsVoiceChatOpen] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeakerMuted, setIsSpeakerMuted] = useState(false);
  const [isGeminiSpeaking, setIsGeminiSpeaking] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [chatMessages, setChatMessages] = useState([
    { sender: 'Ava (AI Agent)', text: "Hi, I'm Ava! Ask me anything about studying abroad or setting up your consultation." }
  ]);
  const [inputMessage, setInputMessage] = useState('');

  const recognitionRef = useRef(null);
  const timerRef = useRef(null);

  // --- Voice Call Timer & Recognition Lifecycle ---
  useEffect(() => {
    if (isVoiceChatOpen) {
      timerRef.current = setInterval(() => setCallDuration(prev => prev + 1), 1000);
      setupSpeechRecognition();
    } else {
      clearInterval(timerRef.current);
      setCallDuration(0);
      stopSpeechRecognition();
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    }
    return () => clearInterval(timerRef.current);
  }, [isVoiceChatOpen]);

  const setupSpeechRecognition = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => {
      if (isVoiceChatOpen && !isMuted) {
        try { recognition.start(); } catch (e) {}
      } else {
        setIsListening(false);
      }
    };

    recognition.onresult = (event) => {
      const transcript = event.results[event.results.length - 1][0].transcript.trim();
      if (transcript) handleUserVoiceInput(transcript);
    };

    recognitionRef.current = recognition;
    try { recognition.start(); } catch (e) {}
  };

  const stopSpeechRecognition = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };

  const toggleMuteMicrophone = () => {
    if (isMuted) {
      setIsMuted(false);
      if (recognitionRef.current) recognitionRef.current.start();
    } else {
      setIsMuted(true);
      if (recognitionRef.current) recognitionRef.current.stop();
    }
  };

  // --- Gemini API Processing ---
  const processGeminiVoiceResponse = async (userText) => {
    setChatMessages(prev => [...prev, { sender: 'You', text: userText }]);

    try {
      const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;

      const systemInstruction = `You are Ava, a helpful AI assistant for Santa Monica Study Abroad / vOffice.io.
      - Provide direct, concise answers (1-3 sentences).
      - If user speaks Malayalam, Hindi, or any Indian language, respond fluently in that language.
      - Support with study abroad, visa info, and virtual bookings.`;

      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-3.6-flash:generateContent?key=${GEMINI_API_KEY}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contents: [{ role: 'user', parts: [{ text: `${systemInstruction}\n\nUser Question: ${userText}` }] }]
          })
        }
      );

      const data = await response.json();
      if (response.ok && data.candidates?.[0]?.content?.parts?.[0]?.text) {
        const aiReply = data.candidates[0].content.parts[0].text.trim();
        setChatMessages(prev => [...prev, { sender: 'Ava (AI Agent)', text: aiReply }]);
        speakText(aiReply);
      } else {
        throw new Error(data.error?.message || "Failed request");
      }
    } catch (err) {
      console.error("[Gemini Error]", err);
      let fallbackReply = "I can help with study abroad admissions, visa processing, or scheduling a virtual session. What can I clarify for you?";
      if (/malayalam|മലയാളം/i.test(userText)) {
        fallbackReply = "തീർച്ചയായും, വിദേശ പഠനത്തെക്കുറിച്ച് എന്താണ് അറിയേണ്ടത്?";
      }
      setChatMessages(prev => [...prev, { sender: 'Ava (AI Agent)', text: fallbackReply }]);
      speakText(fallbackReply);
    }
  };

  const speakText = (text) => {
    if (isSpeakerMuted || !('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.95;
    utterance.pitch = 1.1;

    const voices = window.speechSynthesis.getVoices();
    const isMalayalam = /[\u0D00-\u0D7F]/.test(text) || /malayalam/i.test(text);

    let chosenVoice = isMalayalam 
      ? voices.find(v => v.lang.includes('ml') || v.lang.includes('hi'))
      : voices.find(v => (v.name.includes('Female') || v.name.includes('Google UK English Female') || v.name.includes('Samantha') || v.name.includes('Zira')) && !v.name.includes('Male'));

    if (chosenVoice) utterance.voice = chosenVoice;

    utterance.onstart = () => setIsGeminiSpeaking(true);
    utterance.onend = () => setIsGeminiSpeaking(false);
    utterance.onerror = () => setIsGeminiSpeaking(false);

    window.speechSynthesis.speak(utterance);
  };

  const handleUserVoiceInput = (text) => processGeminiVoiceResponse(text);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;
    const text = inputMessage;
    setInputMessage('');
    processGeminiVoiceResponse(text);
  };

  // --- Helper Functions ---
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const isSunday = (dateString) => {
    if (!dateString) return false;
    return new Date(dateString).getDay() === 0;
  };

  const parseSlotTimeToMinutes = (slotStr) => {
    const [time, modifier] = slotStr.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
    if (hours === 12) hours = 0;
    if (modifier === 'PM') hours += 12;
    return hours * 60 + minutes;
  };

  const getFilteredSlots = () => {
    const today = new Date();
    const localTodayStr = getTodayLocalString();
    if (selectedDate === localTodayStr) {
      const currentMinutes = today.getHours() * 60 + today.getMinutes();
      return AVAILABLE_SLOTS.filter(slot => parseSlotTimeToMinutes(slot) > currentMinutes);
    }
    return AVAILABLE_SLOTS;
  };

  const getISOStrings = (dateStr, slotStr) => {
    const [time, modifier] = slotStr.split(' ');
    let [hours, minutes] = time.split(':');
    if (hours === '12') hours = '00';
    if (modifier === 'PM') hours = parseInt(hours, 10) + 12;

    const startDateTime = new Date(`${dateStr}T${hours.toString().padStart(2, '0')}:${minutes}:00`);
    const endDateTime = new Date(startDateTime.getTime() + 30 * 60 * 1000);

    return { start: startDateTime.toISOString(), end: endDateTime.toISOString() };
  };

  // --- Form Submission Pipeline ---
  const handleBooking = async (e) => {
    e.preventDefault();
    if (!selectedSlot || isSunday(selectedDate)) return;

    setLoading(true);

    try {
      const { start, end } = getISOStrings(selectedDate, selectedSlot);
      let GOOGLE_MEET_LINK = ""; 

      try {
        const backendUrl = '/api/create-meet';
        const meetResponse = await fetch(backendUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            summary: `${formData.name} - Virtual Counseling Session`,
            startTime: start,
            endTime: end
          })
        });

        if (!meetResponse.ok) throw new Error('Backend error');
        const meetData = await meetResponse.json();
        GOOGLE_MEET_LINK = meetData.meetLink;
      } catch (meetErr) {
        console.error('[Google Meet Error]', meetErr);
        GOOGLE_MEET_LINK = "https://meet.google.com/mock-fallback-link";
      }

      let teamList = [];
      try {
        const daysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
        const dateObj = new Date(selectedDate + "T00:00:00");
        const dayName = daysOfWeek[dateObj.getDay()];

        const teamResponse = await fetch(`https://api.santamonicaedu.in/VirtualEmployeeTeamList?virtualOfficeDay=${dayName}`);
        if (teamResponse.ok) teamList = await teamResponse.json();
      } catch (teamErr) {
        console.error('[Team List Error]', teamErr);
      }

      try {
        const leadPayload = {
          name: formData.name ? formData.name.trim() : "",
          Email: formData.email ? formData.email.trim() : "",
          Phone: formData.phone ? formData.phone.trim() : "",
          DesiredCourse: formData.highestQualification || "Not Specified",
          Remarks: `Country Pref: ${formData.countryOfChoice || 'N/A'}. Date: ${selectedDate} @ ${selectedSlot}. Meet Link: ${GOOGLE_MEET_LINK}`,
          label: "15235"
        };

        await fetch('https://api.santamonicaedu.in/Lead', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(leadPayload)
        });
      } catch (leadError) {
        console.error('[Lead API Error]', leadError);
      }

      const emailParams = {
        email: formData.email ? formData.email.trim() : "",
        to_name: formData.name ? formData.name.trim() : "Valued Client",
        scheduled_date: selectedDate,
        scheduled_time: selectedSlot,
        zoom_link: GOOGLE_MEET_LINK,
        contact_phone: formData.phone
      };

      if (emailParams.email) {
        try {
          await emailjs.send('service_oqj16oo', 'template_6dca99f', emailParams, 'bYAgUcHBl6iUA90u1');
        } catch (emailError) {
          console.error('[EmailJS Error]', emailError);
        }
      }

      const customParams = [
        { name: "1", value: formData.name || "N/A" },
        { name: "2", value: formData.phone || "N/A" },
        { name: "3", value: formData.email || "N/A" },
        { name: "4", value: formData.state || "N/A" },
        { name: "5", value: formData.highestQualification || "N/A" },
        { name: "6", value: formData.countryOfChoice || "Not Specified" },
        { name: "7", value: formData.academicDetails || "None provided" },
        { name: "8", value: formData.hasLanguageProficiency },
        { name: "9", value: formData.hasLanguageProficiency === 'Yes' ? formData.languageCertificateType : "N/A" },
        { name: "10", value: selectedDate },
        { name: "11", value: selectedSlot },
        { name: "12", value: GOOGLE_MEET_LINK }
      ];

      let formattedCustomerPhone = formData.phone ? formData.phone.replace(/\D/g, '') : '';
      if (formattedCustomerPhone.length === 10) formattedCustomerPhone = `91${formattedCustomerPhone}`;

      const receivers = teamList.length > 0 
        ? teamList.map(member => ({ whatsappNumber: `91${member.employeePhone}`, customParams }))
        : [{ whatsappNumber: '919446005179', customParams }];

      const watiAuthToken = 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1bmlxdWVfbmFtZSI6ImFkbWluQHNhbnRhbW9uaWNhZWR1LmluIiwibmFtZWlkIjoiYWRtaW5Ac2FudGFtb25pY0FlZHUuaW4iLCJlbWFpbCI6ImFkbWluQHNhbnRhbW9uaWNhZWR1LmluIiwiYXV0aF90aW1lIjoiMDYvMjpvMjAyNiAwOToyOTo0OCIsInRlbmFudF9pZCI6IjMwIiwiZGJfbmFtZSI6Im10LXByb2QtVGVuYW50cyIsImh0dHA6Ly9zY2hlbWFzLm1pY3Jvc29mdC5jb20vd3MvMjAwOC8wNi9pZGVudGl0eS9jbGFpbXMvcm9sZSI6IkFETUlOSVNUUkFUT1IiLCJleHAiOjI1MzQwMjMwMDgwMCwiaXNzIjoiQ2xhcmVfQUkiLCJhdWQiOiJDbGFyZV9BSSJ9.yWbcsHa4sLD2ni0x_11tIZ47U3RQhLoFA9X2m6urdgs';

      await fetch('https://live-mt-server.wati.io/30/api/v1/sendTemplateMessages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': watiAuthToken },
        body: JSON.stringify({ template_name: 'staff_virtual_office_consultation_1', broadcast_name: 'Virtual_Consultation_Staff', receivers })
      });

      await fetch('https://live-mt-server.wati.io/30/api/v1/sendTemplateMessages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': watiAuthToken },
        body: JSON.stringify({ template_name: 'staff_virtual_customer_appointment_confirmation', broadcast_name: 'Virtual_Consultation_Customer', receivers: [{ whatsappNumber: formattedCustomerPhone, customParams }] })
      });

      setBookingResult({ meetLink: GOOGLE_MEET_LINK, date: selectedDate, time: selectedSlot });
    } catch (error) {
      console.error('Execution Fault:', error);
      alert('Failed to complete notifications, but session is logged.');
    } finally {
      setLoading(false);
    }
  };

  const activeSlots = getFilteredSlots();

  return (
    <div style={{ position: 'relative', minHeight: '100vh', backgroundColor: '#0A0E1A', color: '#F1F5F9', fontFamily: 'Inter, system-ui, -apple-system, sans-serif', paddingBottom: '120px' }}>
      
      {/* --- RESPONSIVE HEADER --- */}
      <header style={{
        width: '100%',
        backgroundColor: '#0F172A',
        borderBottom: '1px solid #1E293B',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        backdropFilter: 'blur(10px)'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: isMobile ? '0.75rem 1rem' : '0.85rem 1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          {/* Logo & Brand */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <div style={{
              width: isMobile ? '34px' : '40px',
              height: isMobile ? '34px' : '40px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #2563EB, #4F46E5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
              flexShrink: 0
            }}>
              <LogoIcon size={isMobile ? 18 : 22} color="#FFFFFF" />
            </div>
            <div>
              <div style={{ fontSize: isMobile ? '0.95rem' : '1.15rem', fontWeight: '800', color: '#FFFFFF', letterSpacing: '-0.02em', lineHeight: '1.2' }}>
                Santamonica <span style={{ color: '#3B82F6' }}>Study Abroad</span>
              </div>
              {!isMobile && (
                <div style={{ fontSize: '0.7rem', color: '#64748B', fontWeight: '500' }}>
                   Virtual Advisory Portal
                </div>
              )}
            </div>
          </div>

          {/* Desktop Nav Links */}
          {!isMobile && (
            <nav style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
              <a href="#booking" style={{ color: '#94A3B8', textDecoration: 'none', fontSize: '0.88rem', fontWeight: '500' }}>Book Consultation</a>
              <a href="#destinations" style={{ color: '#94A3B8', textDecoration: 'none', fontSize: '0.88rem', fontWeight: '500' }}>Destinations</a>
              <a href="#services" style={{ color: '#94A3B8', textDecoration: 'none', fontSize: '0.88rem', fontWeight: '500' }}>Visa Guidance</a>
            </nav>
          )}

          {/* Action Callout Button */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {!isMobile && (
              <a href="tel:+919446005179" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#CBD5E1', textDecoration: 'none', fontSize: '0.85rem', fontWeight: '600' }}>
                <Phone size={15} color="#3B82F6" /> Helpline Support
              </a>
            )}
            <button 
              onClick={() => setIsVoiceChatOpen(true)}
              style={{
                backgroundColor: 'rgba(37, 99, 235, 0.15)',
                border: '1px solid rgba(59, 130, 246, 0.4)',
                color: '#60A5FA',
                padding: isMobile ? '0.4rem 0.75rem' : '0.5rem 1rem',
                borderRadius: '8px',
                fontSize: isMobile ? '0.75rem' : '0.82rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem'
              }}
            >
              <Bot size={14} /> {isMobile ? "AI Agent" : "Speak to AI Assistant"}
            </button>

            {/* Mobile Hamburger Toggle */}
            {isMobile && (
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)} 
                style={{ background: 'none', border: 'none', color: '#F1F5F9', padding: '0.2rem', cursor: 'pointer' }}
              >
                {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            )}
          </div>
        </div>

        {/* Mobile Dropdown Nav */}
        {isMobile && mobileMenuOpen && (
          <div style={{ padding: '0.85rem 1.25rem', backgroundColor: '#0B0F19', borderTop: '1px solid #1E293B', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            <a href="#booking" onClick={() => setMobileMenuOpen(false)} style={{ color: '#E2E8F0', textDecoration: 'none', fontSize: '0.9rem', fontWeight: '500' }}>Book Consultation</a>
            <a href="#destinations" onClick={() => setMobileMenuOpen(false)} style={{ color: '#E2E8F0', textDecoration: 'none', fontSize: '0.9rem', fontWeight: '500' }}>Destinations</a>
            <a href="#services" onClick={() => setMobileMenuOpen(false)} style={{ color: '#E2E8F0', textDecoration: 'none', fontSize: '0.9rem', fontWeight: '500' }}>Visa Guidance</a>
            <a href="tel:+919446005179" style={{ color: '#60A5FA', textDecoration: 'none', fontSize: '0.9rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Phone size={14} /> Call Helpline (+91 4844140999)
            </a>
          </div>
        )}
      </header>

      {/* Main Container */}
      <div id="booking" style={{ maxWidth: '1100px', margin: '0 auto', padding: isMobile ? '1.5rem 1rem' : '3rem 1.5rem' }}>
        
        {/* Top AI Badge */}
        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.4rem',
            padding: '0.35rem 0.85rem',
            borderRadius: '9999px',
            backgroundColor: 'rgba(30, 58, 138, 0.35)',
            border: '1px solid rgba(59, 130, 246, 0.4)',
            color: '#60A5FA',
            fontSize: isMobile ? '0.72rem' : '0.8rem',
            fontWeight: '600',
            letterSpacing: '0.02em'
          }}>
            <Sparkles size={13} /> Counselling, Admission & Visa Advisory
          </span>
        </div>

        {/* Header Heading */}
        <div style={{ textAlign: 'center', marginBottom: isMobile ? '2rem' : '3.5rem' }}>
          <h1 style={{ fontSize: isMobile ? '1.8rem' : '2.75rem', fontWeight: '800', color: '#FFFFFF', margin: '0 0 0.5rem 0', letterSpacing: '-0.02em', lineHeight: '1.2' }}>
            Schedule Your <span style={{ background: 'linear-gradient(90deg, #60A5FA, #818CF8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Virtual Consultation</span>
          </h1>
          <p style={{ color: '#94A3B8', fontSize: isMobile ? '0.88rem' : '1.05rem', margin: 0, fontWeight: '400', lineHeight: '1.4' }}>
            Get personalized guidance for universities, scholarships, and visa processing with our certified senior counselors.
          </p>
        </div>

        {!bookingResult ? (
          <form onSubmit={handleBooking} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1.35fr', gap: isMobile ? '1.25rem' : '2rem' }}>
            
            {/* LEFT CARD: Step 1 Date & Time */}
            <div style={{ backgroundColor: '#111827', border: '1px solid #1E293B', borderRadius: '16px', padding: isMobile ? '1.25rem' : '1.75rem', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
                <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: isMobile ? '1.05rem' : '1.15rem', fontWeight: '700', color: '#F8FAFC', margin: 0 }}>
                  <CalendarIcon size={18} color="#3B82F6" /> 1. Date & Time
                </h2>
                <span style={{ color: '#64748B', fontSize: '0.78rem', fontWeight: '500' }}>Step 1 of 2</span>
              </div>

              {/* Timezone Selection */}
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', color: '#94A3B8', marginBottom: '0.35rem' }}>
                  <Globe size={13} /> Timezone
                </label>
                <select style={{ ...inputStyle, cursor: 'pointer' }}>
                  <option value="Asia/Calcutta">Asia/Calcutta (Auto-detected)</option>
                  <option value="Europe/London">Europe/London (GMT)</option>
                  <option value="America/New_York">America/New_York (EST)</option>
                </select>
              </div>

              {/* Date Input */}
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', color: '#94A3B8', marginBottom: '0.35rem' }}>Select Date</label>
                <input 
                  type="date" 
                  value={selectedDate} 
                  min={getTodayLocalString()}
                  onChange={(e) => { setSelectedDate(e.target.value); setSelectedSlot(null); }}
                  style={{ ...inputStyle, cursor: 'pointer' }}
                />
                <span style={{ display: 'block', marginTop: '0.4rem', fontSize: '0.72rem', color: '#64748B' }}>
                  Note: Office operates Monday to Saturday.
                </span>
              </div>

              {/* Time Slots Grid */}
              <div style={{ marginBottom: '1.25rem', flex: 1 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', color: '#94A3B8', marginBottom: '0.75rem' }}>
                  <Clock size={13} /> Available Time Slots
                </label>

                {isSunday(selectedDate) ? (
                  <div style={{ padding: '1rem', backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)', borderRadius: '8px', color: '#FCA5A5', textAlign: 'center', fontSize: '0.85rem' }}>
                    Office Closed on Sundays
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr', gap: '0.6rem' }}>
                    {activeSlots.map(slot => (
                      <button
                        key={slot}
                        type="button"
                        onClick={() => setSelectedSlot(slot)}
                        style={{
                          padding: '0.65rem',
                          borderRadius: '8px',
                          border: selectedSlot === slot ? '2px solid #3B82F6' : '1px solid #1E293B',
                          backgroundColor: selectedSlot === slot ? '#2563EB' : '#0F172A',
                          color: selectedSlot === slot ? '#FFFFFF' : '#CBD5E1',
                          cursor: 'pointer',
                          fontWeight: '600',
                          fontSize: '0.82rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.4rem',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        <Clock size={12} opacity={selectedSlot === slot ? 1 : 0.6} />
                        {slot}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Session Callout */}
              <div style={{ padding: '0.85rem 1rem', backgroundColor: 'rgba(30, 58, 138, 0.25)', border: '1px solid rgba(59, 130, 246, 0.2)', borderRadius: '10px', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <ShieldCheck size={20} color="#3B82F6" style={{ flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#F1F5F9' }}>Complimentary 30-Min Session</div>
                  <div style={{ fontSize: '0.72rem', color: '#94A3B8' }}>Instant video connection details sent upon submission.</div>
                </div>
              </div>

            </div>

            {/* RIGHT CARD: Step 2 Student & Academic Profile */}
            <div style={{ backgroundColor: '#111827', border: '1px solid #1E293B', borderRadius: '16px', padding: isMobile ? '1.25rem' : '1.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
                <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: isMobile ? '1.05rem' : '1.15rem', fontWeight: '700', color: '#F8FAFC', margin: 0 }}>
                  <User size={18} color="#3B82F6" /> 2. Student & Academic Profile
                </h2>
                <span style={{ color: '#64748B', fontSize: '0.78rem', fontWeight: '500' }}>Step 2 of 2</span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '0.85rem' : '1rem', marginBottom: '0.85rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: '#94A3B8' }}>Full Name *</label>
                  <input type="text" required placeholder="e.g. Rahul Sharma" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} style={inputStyle} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: '#94A3B8' }}>Email Address *</label>
                  <input type="email" required placeholder="rahul@example.com" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} style={inputStyle} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '0.85rem' : '1rem', marginBottom: '0.5rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: '#94A3B8' }}>Contact Number *</label>
                  <input type="tel" required placeholder="+91 98765 43210" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} style={inputStyle} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: '#94A3B8' }}>State / Location *</label>
                  <input type="text" required placeholder="e.g. Maharashtra" value={formData.state} onChange={e => setFormData({...formData, state: e.target.value})} style={inputStyle} />
                </div>
              </div>

              <div style={{ marginBottom: '1.1rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.78rem', color: '#94A3B8' }}>
                  <input type="checkbox" defaultChecked style={{ accentColor: '#3B82F6' }} /> Available on WhatsApp
                </label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '0.85rem' : '1rem', marginBottom: '0.85rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: '#94A3B8' }}>Highest Qualification *</label>
                  <select value={formData.highestQualification} onChange={e => setFormData({...formData, highestQualification: e.target.value})} style={{ ...inputStyle, cursor: 'pointer' }}>
                    <option value="Bachelor's Degree">Bachelor's Degree</option>
                    <option value="Master's Degree">Master's Degree</option>
                    <option value="Higher Secondary (12th)">Higher Secondary (12th)</option>
                    <option value="Diploma">Diploma</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: '#94A3B8' }}>Target Country Choice *</label>
                  <select value={formData.countryOfChoice} onChange={e => setFormData({...formData, countryOfChoice: e.target.value})} style={{ ...inputStyle, cursor: 'pointer' }}>
                    <option value="Canada">Canada</option>
                    <option value="UK">United Kingdom</option>
                    <option value="USA">United States</option>
                    <option value="Australia">Australia</option>
                    <option value="Germany">Germany</option>
                    <option value="Ireland">Ireland</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.2fr 1fr', gap: isMobile ? '0.85rem' : '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: '#94A3B8' }}>Language Proficiency</label>
                  <select value={formData.languageCertificateType} onChange={e => setFormData({...formData, languageCertificateType: e.target.value})} style={{ ...inputStyle, cursor: 'pointer' }}>
                    <option value="IELTS">IELTS</option>
                    <option value="PTE">PTE</option>
                    <option value="TOEFL">TOEFL</option>
                    <option value="DUOLINGO">Duolingo</option>
                    <option value="NONE">None</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: '#94A3B8' }}>Academic Scores / GPA</label>
                  <input type="text" placeholder="e.g. 78% or 3.6 GPA" value={formData.academicDetails} onChange={e => setFormData({...formData, academicDetails: e.target.value})} style={inputStyle} />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !selectedSlot || isSunday(selectedDate)}
                style={{
                  width: '100%',
                  padding: '0.95rem',
                  background: (!selectedSlot || isSunday(selectedDate)) ? '#334155' : 'linear-gradient(90deg, #3B82F6, #6366F1)',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '10px',
                  fontWeight: '700',
                  fontSize: '0.95rem',
                  cursor: (!selectedSlot || isSunday(selectedDate)) ? 'not-allowed' : 'pointer',
                  boxShadow: '0 4px 14px 0 rgba(59, 130, 246, 0.39)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                <CheckCircle size={18} />
                {loading ? "Confirming..." : "Confirm Consultation Booking"}
              </button>

              <div style={{ textAlign: 'center', marginTop: '0.85rem', fontSize: '0.72rem', color: '#64748B' }}>
                Free booking • Instant Google Meet confirmation
              </div>

            </div>

          </form>
        ) : (
          /* Confirmation State */
          <div style={{ maxWidth: '520px', margin: '2rem auto', backgroundColor: '#111827', border: '1px solid #1E293B', padding: isMobile ? '1.5rem' : '2.5rem', borderRadius: '16px', textAlign: 'center' }}>
            <CheckCircle size={48} color="#10B981" style={{ margin: '0 auto 1rem auto' }} />
            <h2 style={{ fontSize: '1.4rem', color: '#FFFFFF', margin: '0 0 0.5rem 0' }}>Consultation Confirmed!</h2>
            <p style={{ color: '#94A3B8', fontSize: '0.88rem', marginBottom: '1.5rem' }}>
              Your Google Meet room link and WhatsApp confirmation details have been dispatched.
            </p>
            <div style={{ backgroundColor: '#0F172A', border: '1px solid #1E293B', padding: '0.85rem', borderRadius: '8px', marginBottom: '1.5rem', color: '#60A5FA', fontWeight: '600', fontSize: '0.85rem' }}>
              Date: {bookingResult.date} | Time: {bookingResult.time}
            </div>
            <a href={bookingResult.meetLink} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '100%', padding: '0.85rem', backgroundColor: '#2563EB', color: '#fff', textDecoration: 'none', borderRadius: '8px', fontWeight: '600', fontSize: '0.9rem' }}>
              <Video size={18} style={{ marginRight: '0.5rem' }} /> Launch Video Room
            </a>
          </div>
        )}

      </div>

      {/* --- FLOATING AI BOTTOM BAR --- */}
      <div 
        onClick={() => setIsVoiceChatOpen(true)}
        style={{
          position: 'fixed',
          bottom: '16px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#0F172A',
          color: '#FFFFFF',
          padding: '6px 12px',
          borderRadius: '9999px',
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? '8px' : '12px',
          cursor: 'pointer',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 8px 10px -6px rgba(0, 0, 0, 0.5)',
          border: '1px solid #1E293B',
          zIndex: 999,
          maxWidth: '92vw'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '26px', height: '26px', borderRadius: '50%', backgroundColor: '#6366F1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Bot size={15} color="#FFF" />
          </div>
          <span style={{ fontWeight: '700', fontSize: '0.82rem' }}>Ava AI</span>
          {!isMobile && (
            <span style={{ fontSize: '0.7rem', color: '#94A3B8', backgroundColor: '#1E293B', padding: '2px 8px', borderRadius: '12px' }}></span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ fontSize: '0.78rem', padding: '0.3rem 0.65rem', backgroundColor: '#1E293B', borderRadius: '9999px', color: '#E2E8F0', fontWeight: '500' }}>Chat</span>
          <span style={{ fontSize: '0.78rem', padding: '0.3rem 0.65rem', backgroundColor: '#2563EB', borderRadius: '9999px', color: '#FFF', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Mic size={12} /> Voice Call
          </span>
        </div>
      </div>

      {/* --- AI VOICE & CHAT MODAL OVERLAY --- */}
      {isVoiceChatOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000,
          padding: isMobile ? '0.5rem' : '1rem'
        }}>
          <div style={{
            position: 'relative',
            backgroundColor: '#0B0F19',
            borderRadius: '20px',
            width: '100%',
            maxWidth: '950px',
            height: isMobile ? '92vh' : '580px',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
            border: '1px solid #1E293B'
          }}>
            
            <button 
              onClick={() => setIsVoiceChatOpen(false)}
              style={{
                position: 'absolute',
                top: '0.8rem',
                right: '0.8rem',
                border: 'none',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '50%',
                width: '34px',
                height: '34px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: '#fff',
                zIndex: 10
              }}
            >
              <X size={18} />
            </button>

            {/* Avatar Screen */}
            <div style={{ position: 'relative', flex: isMobile ? 'none' : '1.2', padding: isMobile ? '1.5rem 1rem' : '2rem', backgroundColor: '#020617', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{
                position: 'absolute',
                top: '0.8rem',
                left: '0.8rem',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                padding: '0.25rem 0.65rem',
                borderRadius: '9999px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                color: '#fff',
                fontSize: '0.72rem',
                fontWeight: '600'
              }}>
                <span style={{ width: '7px', height: '7px', backgroundColor: isListening ? '#10B981' : '#EF4444', borderRadius: '50%' }}></span>
                LIVE {formatTime(callDuration)}
              </div>

              <div style={{ width: isMobile ? '80px' : '120px', height: isMobile ? '80px' : '120px', borderRadius: '50%', backgroundColor: '#0F172A', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '3px solid #3B82F6', marginBottom: '0.75rem', boxShadow: '0 0 25px rgba(59, 130, 246, 0.35)' }}>
                <Bot size={isMobile ? 40 : 60} color="#3B82F6" />
              </div>

              <h3 style={{ color: '#fff', margin: '0 0 0.2rem 0', fontSize: isMobile ? '1.1rem' : '1.25rem' }}>Ava (Gemini AI)</h3>
              <p style={{ color: '#94A3B8', fontSize: '0.8rem', margin: '0 0 1rem 0' }}>{isGeminiSpeaking ? "Speaking..." : isListening ? "Listening..." : "Voice Standby"}</p>

              <div style={{ display: 'flex', gap: '0.85rem' }}>
                <button onClick={toggleMuteMicrophone} style={{ width: '40px', height: '40px', borderRadius: '50%', border: 'none', backgroundColor: isMuted ? '#EF4444' : 'rgba(255,255,255,0.12)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                  {isMuted ? <MicOff size={18} /> : <Mic size={18} />}
                </button>
                <button onClick={() => setIsSpeakerMuted(!isSpeakerMuted)} style={{ width: '40px', height: '40px', borderRadius: '50%', border: 'none', backgroundColor: isSpeakerMuted ? '#EF4444' : 'rgba(255,255,255,0.12)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                  {isSpeakerMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                </button>
              </div>
            </div>

            {/* Chat Screen */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: isMobile ? '1rem' : '1.5rem', backgroundColor: '#0B0F19', borderLeft: isMobile ? 'none' : '1px solid #1E293B', borderTop: isMobile ? '1px solid #1E293B' : 'none' }}>
              <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.85rem', paddingRight: '0.25rem' }}>
                {chatMessages.map((msg, idx) => (
                  <div key={idx} style={{ alignSelf: msg.sender === 'You' ? 'flex-end' : 'flex-start', maxWidth: '88%' }}>
                    <div style={{ fontSize: '0.7rem', color: '#64748B', marginBottom: '0.2rem', textAlign: msg.sender === 'You' ? 'right' : 'left' }}>
                      {msg.sender}
                    </div>
                    <div style={{
                      padding: '0.7rem 0.9rem',
                      borderRadius: msg.sender === 'You' ? '14px 14px 2px 14px' : '14px 14px 14px 2px',
                      backgroundColor: msg.sender === 'You' ? '#2563EB' : '#1E293B',
                      color: '#fff',
                      fontSize: '0.85rem',
                      lineHeight: '1.4'
                    }}>
                      {msg.text}
                    </div>
                  </div>
                ))}
              </div>

              <form onSubmit={handleSendMessage} style={{ marginTop: '0.85rem', position: 'relative' }}>
                <input 
                  type="text" 
                  placeholder="Ask Ava anything..." 
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem 2.75rem 0.75rem 0.9rem',
                    backgroundColor: '#1E293B',
                    border: '1px solid #334155',
                    borderRadius: '9999px',
                    color: '#fff',
                    fontSize: '0.85rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
                <button type="submit" style={{ position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)', width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#2563EB', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff' }}>
                  <Send size={14} />
                </button>
              </form>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}